import {
    AccountOwnership,
    getApexRegistrationOwnershipLabel,
    getLegacyImAccountOwnershipLabel,
    type InvestmentManagementAccountType,
    type MoneyMapAccountCreateParams,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountWithAccountDetails,
    type MoneyMapApexAccountDetails,
    type MoneyMapBankAccountDetails,
    type MoneyMapIMAccountDetails,
    Portfolio,
    ReadableApexAccountRegistrationType,
    ReadableInvestmentManagementAccountType,
    type RiskLevel,
    type SupportedApexAccountRegistrationType,
} from '@fruitful/types';

export interface MoneyMapAccountHolder {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
}

export interface MoneyMapAccountDetail {
    label: string;
    value: string;
}

const isInvestmentAccount = (
    account: MoneyMapAccountWithAccountDetails,
): boolean =>
    account.type === MoneyMapAccountType.INVESTMENT_MANAGEMENT ||
    account.type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT;

const detail = (
    label: string,
    value: string | null,
): MoneyMapAccountDetail | null => (value != null ? { label, value } : null);

const compactDetails = (
    ...rows: Array<MoneyMapAccountDetail | null>
): MoneyMapAccountDetail[] =>
    rows.filter((row): row is MoneyMapAccountDetail => row != null);

const formatAccountHolderName = (
    holder: MoneyMapAccountHolder,
): string | null => {
    const name = [holder.firstName, holder.lastName]
        .filter((part): part is string => part != null && part.length > 0)
        .join(' ')
        .trim();
    return name.length > 0 ? name : null;
};

const resolveAccountHolderName = (
    primaryUserId: string | undefined,
    accountHolders: MoneyMapAccountHolder[] | undefined,
): string | null => {
    if (
        accountHolders == null ||
        accountHolders.length < 2 ||
        primaryUserId == null
    ) {
        return null;
    }

    const holder = accountHolders.find((member) => member.id === primaryUserId);
    return holder != null ? formatAccountHolderName(holder) : null;
};

const toTooltipOwnership = (ownershipLabel: string | null): string | null =>
    ownershipLabel === 'Individual' ? 'Individual' : ownershipLabel;

const getCardType = ({
    hasPhysicalCard,
    hasVirtualCard,
}: MoneyMapBankAccountDetails): string => {
    if (hasPhysicalCard && hasVirtualCard) {
        return 'Physical + Virtual';
    }
    if (hasVirtualCard) {
        return 'Virtual';
    }
    if (hasPhysicalCard) {
        return 'Physical';
    }
    return 'None';
};

const getMaskedAccountNumber = (accountNumberMasked?: string): string =>
    accountNumberMasked != null && accountNumberMasked.length > 0
        ? ` (*${accountNumberMasked.slice(-4)})`
        : '';

const getInvestmentCreateDefaults = (
    account: MoneyMapAccountWithAccountDetails,
): {
    apexAccountType?: SupportedApexAccountRegistrationType;
    accountType?: InvestmentManagementAccountType;
    portfolio?: Portfolio;
    riskLevel?: RiskLevel;
} => {
    const createParameters = account.createParameters;
    if (createParameters == null) {
        return {};
    }

    if ('apexAccountType' in createParameters) {
        return {
            apexAccountType: createParameters.apexAccountType,
            portfolio: createParameters.apexPortfolio,
            riskLevel: createParameters.apexRiskLevel,
        };
    }

    if ('accountType' in createParameters) {
        return {
            accountType: createParameters.accountType,
            portfolio: createParameters.portfolio,
            riskLevel: createParameters.riskLevel,
        };
    }

    return {};
};

const getReadableInvestmentAccountType = (
    registrationType?: SupportedApexAccountRegistrationType,
    legacyAccountType?: InvestmentManagementAccountType,
): string => {
    if (registrationType != null) {
        return (
            ReadableApexAccountRegistrationType[registrationType] ??
            'Investment account'
        );
    }

    if (legacyAccountType != null) {
        return (
            ReadableInvestmentManagementAccountType[legacyAccountType] ??
            'Investment account'
        );
    }

    return 'Investment account';
};

const getInvestmentAccountContent = (
    account: MoneyMapAccountWithAccountDetails,
    accountHolders?: MoneyMapAccountHolder[],
): {
    subtitle: string;
    details: MoneyMapAccountDetail[];
} => {
    const isApex =
        account.type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT;
    const apexDetails = isApex
        ? (account.accountDetails as MoneyMapApexAccountDetails)
        : undefined;
    const legacyDetails = !isApex
        ? (account.accountDetails as MoneyMapIMAccountDetails)
        : undefined;
    const createDefaults = getInvestmentCreateDefaults(account);

    const registrationType =
        apexDetails?.apexAccountType ?? createDefaults.apexAccountType;
    const legacyAccountType =
        legacyDetails?.accountType ?? createDefaults.accountType;
    const statusPrefix =
        account.status === MoneyMapAccountStatus.PROPOSED ? 'New ' : '';
    const ownershipLabel = toTooltipOwnership(
        isApex
            ? getApexRegistrationOwnershipLabel(registrationType)
            : getLegacyImAccountOwnershipLabel(legacyAccountType),
    );
    const portfolio =
        apexDetails?.apexPortfolio ??
        legacyDetails?.portfolio ??
        createDefaults.portfolio;
    const riskLevel =
        apexDetails?.apexRiskLevel ??
        legacyDetails?.riskLevel ??
        createDefaults.riskLevel;

    return {
        subtitle: `${statusPrefix}Fruitful ${getReadableInvestmentAccountType(
            registrationType,
            legacyAccountType,
        )}`,
        details: compactDetails(
            detail('Ownership', ownershipLabel),
            detail(
                'Account holder',
                ownershipLabel === 'Individual'
                    ? resolveAccountHolderName(
                          account.accountDetails.primaryUserId,
                          accountHolders,
                      )
                    : null,
            ),
            detail(
                'Portfolio',
                portfolio == null
                    ? null
                    : portfolio === Portfolio.CORE
                      ? 'Core'
                      : 'ESG',
            ),
            detail(
                'Risk level',
                riskLevel == null ? null : `${riskLevel} out of 10`,
            ),
        ),
    };
};

const getCashAccountDetailRows = (
    accountDetails: MoneyMapBankAccountDetails,
    accountHolders?: MoneyMapAccountHolder[],
    createParameters?: MoneyMapAccountCreateParams,
): MoneyMapAccountDetail[] => {
    const isJoint = accountDetails.jointUserId != null;
    const isJointPending =
        !isJoint && createParameters?.ownership === AccountOwnership.JOINT;
    const jointLabel = isJointPending
        ? 'Joint (pending)'
        : isJoint
          ? 'Joint'
          : 'Individual';

    return compactDetails(
        { label: 'Ownership', value: jointLabel },
        detail(
            'Account holder',
            isJoint
                ? null
                : resolveAccountHolderName(
                      accountDetails.primaryUserId,
                      accountHolders,
                  ),
        ),
        { label: 'Fruitful Card', value: getCardType(accountDetails) },
    );
};

export const getMoneyMapAccountSubtitle = (
    account: MoneyMapAccountWithAccountDetails,
): string => {
    const statusPrefix =
        account.status === MoneyMapAccountStatus.PROPOSED ? 'New ' : '';

    if (account.type === MoneyMapAccountType.INTERNAL_BANKING) {
        return `${statusPrefix}Fruitful Cash Account`;
    }

    if (isInvestmentAccount(account)) {
        return getInvestmentAccountContent(account).subtitle;
    }

    const { accountDetails } = account;
    const institutionName = accountDetails.institutionName;
    const accountNumber =
        'accountNumberMasked' in accountDetails
            ? getMaskedAccountNumber(accountDetails.accountNumberMasked)
            : '';

    if (institutionName != null && institutionName.length > 0) {
        return `${institutionName}${accountNumber}`;
    }

    return accountNumber.trim().length > 0
        ? accountNumber.trim()
        : 'External account';
};

export const getMoneyMapAccountDetailRows = (
    account: MoneyMapAccountWithAccountDetails,
    accountHolders?: MoneyMapAccountHolder[],
): MoneyMapAccountDetail[] => {
    if (account.type === MoneyMapAccountType.INTERNAL_BANKING) {
        return getCashAccountDetailRows(
            account.accountDetails as MoneyMapBankAccountDetails,
            accountHolders,
            account.createParameters,
        );
    }

    if (isInvestmentAccount(account)) {
        return getInvestmentAccountContent(account, accountHolders).details;
    }

    return [];
};
